import React, { useEffect, useState } from "react";
import {
  Search,
  Star,
  ChevronRight,
  Loader2,
  Filter,
  ShoppingCart,
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import ProductAvailabilityBadge from "./ProductAvailabilityBadge";

// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const BackendUrl = process.env.REACT_APP_Backend_Url;

const stripHtml = (html) => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [types, setTypes] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(12);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedType, setSelectedType] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchInitialData();
  }, []);

  const admin = JSON.parse(localStorage.getItem("AdminEcomme"));
  if (admin) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${admin.token}`;
  }

  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      const [productsRes, typesRes] = await Promise.all([
        axios.get(`${BackendUrl}/api/productsRoutes`),
        axios.get(`${BackendUrl}/api/categoriesRoutes`),
      ]);

      setProducts(productsRes.data.data);
      setTypes(typesRes.data.data);
      setTotalPages(Math.ceil(productsRes.data.data.length / perPage));
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error.response?.data?.message || "Failed to fetch products",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTypeSelect = async (typeId, typeName) => {
    setIsLoading(true);
    setSelectedType(typeName);
    try {
      if (typeName === "All") {
        await fetchInitialData();
        return;
      }

      const response = await axios.get(
        `${BackendUrl}/api/productsRoutes/category/${typeId}`
      );
      setProducts(response.data.products);
      setTotalPages(Math.ceil(response.data.products.length / perPage));
      setCurrentPage(1);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to filter products",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchName.length < 2) {
      toast({
        variant: "warning",
        title: "Warning",
        description: "Search term must be at least 2 characters long",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BackendUrl}/api/productsRoutes/search/${searchName}`
      );
      setProducts(response.data.products);
      setTotalPages(Math.ceil(response.data.products.length / perPage));
      setCurrentPage(1);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to search products",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const paginatedProducts = products.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const ProductCard = ({ product }) => {
    const [imageError, setImageError] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Card
        className="group overflow-hidden transition-all duration-300 border-t-4 border-orange-500 rounded-lg shadow-lg hover:shadow-xl"
        style={{
          borderTopColor: COLORS.orange,
          backgroundColor: COLORS.white,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative aspect-square overflow-hidden">
          {!imageError ? (
            <img
              src={product.image1}
              alt={product.name}
              onError={() => setImageError(true)}
              className="object-cover w-full h-full transform transition-transform duration-500"
              style={{
                transform: isHovered ? "scale(1.1)" : "scale(1.0)",
              }}
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ backgroundColor: COLORS.lightGray }}
            >
              <span className="text-gray-400">No image available</span>
            </div>
          )}
          <div
            className="absolute top-2 right-2 font-semibold"
            // style={{
            //   backgroundColor: COLORS.green,
            //   color: COLORS.white,
            // }}
          >
            <ProductAvailabilityBadge isAvailable={product.isdisponible} />
          </div>
        </div>

        <CardHeader className="space-y-1 p-4">
          <div className="flex justify-between items-center">
            <CardTitle className="line-clamp-1 text-lg font-bold text-gray-800">
              {product.name}
            </CardTitle>
            <div className="flex text-yellow-400">
              {[...Array(5)]?.map((_, i) => (
                <Star key={i} size={14} fill="currentColor" />
              ))}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3 p-4 pt-0">
          <div className="text-2xl font-bold" style={{ color: COLORS.orange }}>
            {product.prix.toLocaleString()} €
          </div>
          <div className="h-12 overflow-hidden" style={{ color: COLORS.gray }}>
            <p className="text-sm line-clamp-2">
              {stripHtml(product.description)}
            </p>
          </div>
        </CardContent>

        <CardFooter className="p-4 pt-0">
          <Button
            asChild
            className="w-full transition-colors rounded-full font-medium"
            style={{
              backgroundColor: isHovered ? COLORS.darkGreen : COLORS.green,
              color: COLORS.white,
            }}
          >
            <Link
              to={`/Admin/ProductDet/${product._id}`}
              className="flex items-center justify-center py-2"
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              View Details
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    );
  };

  const ProductSkeleton = () => (
    <div
      className="rounded-lg overflow-hidden shadow-md border border-gray-200"
      style={{ borderTop: `4px solid ${COLORS.orange}` }}
    >
      <Skeleton className="aspect-square w-full" />
      <div className="p-4 space-y-4">
        <div className="flex justify-between">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-16" />
        </div>
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-10 w-full rounded-full" />
      </div>
    </div>
  );

  return (
    <ScrollArea
      className="h-screen"
      style={{ backgroundColor: COLORS.paleGreen }}
    >
      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Header Section */}
        <div
          className="p-6 rounded-lg shadow-md space-y-6"
          style={{
            backgroundColor: COLORS.white,
            borderLeft: `5px solid ${COLORS.orange}`,
            borderRight: `5px solid ${COLORS.green}`,
          }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <h1
                className="text-3xl font-bold"
                style={{
                  background: `linear-gradient(to right, ${COLORS.orange}, ${COLORS.green})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Products Collection
              </h1>
              <Badge
                className="text-lg font-semibold"
                style={{
                  backgroundColor: COLORS.lightOrange,
                  color: COLORS.orange,
                }}
              >
                {selectedType}
              </Badge>
            </div>

            <form
              onSubmit={handleSearch}
              className="flex items-center gap-2 w-full md:w-auto"
            >
              <Input
                type="search"
                placeholder="Search products..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="max-w-sm rounded-full border-2 focus:ring-2 focus:ring-opacity-50"
                style={{
                  borderColor: COLORS.green,
                  backgroundColor: COLORS.white,
                  color: COLORS.gray,
                }}
              />
              <Button
                type="submit"
                className="rounded-full"
                style={{
                  backgroundColor: COLORS.orange,
                  color: COLORS.white,
                }}
              >
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </form>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            <Button
              onClick={() => handleTypeSelect(null, "All")}
              className="whitespace-nowrap font-medium rounded-full text-sm px-4 py-2"
              style={{
                backgroundColor:
                  selectedType === "All" ? COLORS.green : COLORS.white,
                color: selectedType === "All" ? COLORS.white : COLORS.gray,
                border: `1px solid ${COLORS.green}`,
              }}
            >
              <Filter className="h-4 w-4 mr-2" />
              All Products
            </Button>
            {types?.map((type) => (
              <Button
                key={type._id}
                onClick={() => handleTypeSelect(type._id, type.name)}
                className="whitespace-nowrap font-medium rounded-full text-sm px-4 py-2"
                style={{
                  backgroundColor:
                    selectedType === type.name ? COLORS.orange : COLORS.white,
                  color:
                    selectedType === type.name ? COLORS.white : COLORS.gray,
                  border: `1px solid ${COLORS.orange}`,
                }}
              >
                {type.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Products count */}
        <div className="flex justify-between items-center px-4">
          <p
            className="text-sm font-medium"
            style={{ color: COLORS.darkGreen }}
          >
            Showing {paginatedProducts.length} of {products.length} products
          </p>
          {isLoading && (
            <div className="flex items-center" style={{ color: COLORS.orange }}>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading...
            </div>
          )}
        </div>

        {/* Products Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)]?.map((_, index) => (
              <ProductSkeleton key={index} />
            ))}
          </div>
        ) : (
          <>
            {paginatedProducts.length === 0 ? (
              <div className="py-16 text-center">
                <div
                  className="mx-auto bg-white p-8 rounded-lg shadow-md max-w-md"
                  style={{ borderTop: `4px solid ${COLORS.orange}` }}
                >
                  <Search
                    className="h-16 w-16 mx-auto mb-4"
                    style={{ color: COLORS.gray }}
                  />
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: COLORS.gray }}
                  >
                    No Products Found
                  </h3>
                  <p className="mb-4" style={{ color: COLORS.gray }}>
                    We couldn't find any products matching your criteria.
                  </p>
                  <Button
                    onClick={() => {
                      setSearchName("");
                      handleTypeSelect(null, "All");
                    }}
                    className="rounded-full font-medium"
                    style={{
                      backgroundColor: COLORS.green,
                      color: COLORS.white,
                    }}
                  >
                    View All Products
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {paginatedProducts?.map((product) => (
                  <ProductCard key={product._id} product={product} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() =>
                          setCurrentPage((p) => Math.max(1, p - 1))
                        }
                        disabled={currentPage === 1}
                        style={{
                          color:
                            currentPage === 1 ? COLORS.gray : COLORS.orange,
                          opacity: currentPage === 1 ? 0.5 : 1,
                        }}
                      />
                    </PaginationItem>

                    {totalPages <= 5 ? (
                      // Show all pages if 5 or fewer
                      [...Array(totalPages)]?.map((_, i) => (
                        <PaginationItem key={i + 1}>
                          <PaginationLink
                            onClick={() => setCurrentPage(i + 1)}
                            style={{
                              backgroundColor:
                                currentPage === i + 1
                                  ? COLORS.green
                                  : "transparent",
                              color:
                                currentPage === i + 1
                                  ? COLORS.white
                                  : COLORS.gray,
                              fontWeight: "bold",
                            }}
                          >
                            {i + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ))
                    ) : (
                      // Show limited pages for larger sets
                      <>
                        {currentPage > 2 && (
                          <PaginationItem>
                            <PaginationLink onClick={() => setCurrentPage(1)}>
                              1
                            </PaginationLink>
                          </PaginationItem>
                        )}

                        {currentPage > 3 && (
                          <PaginationItem>
                            <span className="px-2">...</span>
                          </PaginationItem>
                        )}

                        {currentPage > 1 && (
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => setCurrentPage(currentPage - 1)}
                            >
                              {currentPage - 1}
                            </PaginationLink>
                          </PaginationItem>
                        )}

                        <PaginationItem>
                          <PaginationLink
                            isActive={true}
                            style={{
                              backgroundColor: COLORS.green,
                              color: COLORS.white,
                              fontWeight: "bold",
                            }}
                          >
                            {currentPage}
                          </PaginationLink>
                        </PaginationItem>

                        {currentPage < totalPages && (
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => setCurrentPage(currentPage + 1)}
                            >
                              {currentPage + 1}
                            </PaginationLink>
                          </PaginationItem>
                        )}

                        {currentPage < totalPages - 2 && (
                          <PaginationItem>
                            <span className="px-2">...</span>
                          </PaginationItem>
                        )}

                        {currentPage < totalPages - 1 && (
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => setCurrentPage(totalPages)}
                            >
                              {totalPages}
                            </PaginationLink>
                          </PaginationItem>
                        )}
                      </>
                    )}

                    <PaginationItem>
                      <PaginationNext
                        onClick={() =>
                          setCurrentPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={currentPage === totalPages}
                        style={{
                          color:
                            currentPage === totalPages
                              ? COLORS.gray
                              : COLORS.orange,
                          opacity: currentPage === totalPages ? 0.5 : 1,
                        }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </div>
    </ScrollArea>
  );
}
